// Fix: Populated file with ArtistFormModal component
import React, { useState, useEffect } from 'react';
import { Artist, Project } from '../types';
import { SPECIALIZATIONS, SOFTWARE, ICONS } from '../constants';
import { getAvatarColor, getInitials, getAvatarUrl } from '../utils/avatarUtils';
import { formatProjectDateRange } from '../utils/dateUtils';
import { useTranslation } from '../hooks/useTranslation';
import Modal from './ui/Modal';
import Input from './ui/Input';
import Button from './ui/Button';
import Select from './ui/Select';
import AvailabilityCalendar from './AvailabilityCalendar';
import Textarea from './ui/Textarea';
import ProfileAccessSection from './ProfileAccessSection';

interface ArtistFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (artist: Artist, avatarFile?: File) => void;
  artistToEdit: Artist | null;
  projects: Project[];
  onAccessUpdated?: (artist: Artist) => void;
}

const ArtistFormModal: React.FC<ArtistFormModalProps> = ({ isOpen, onClose, onSave, artistToEdit, projects, onAccessUpdated }) => {
  const { t } = useTranslation();
  
  // Helper function to get translated specialization
  const getSpecializationName = (spec: string) => {
    const key = `specializations.${spec.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase()}`;
    const translated = t(key);
    return translated === key ? spec : translated; // fallback to original if not found
  };
  
  const getInitialState = (): Omit<Artist, 'id'> & { id?: string } => {
    // Fix: Added missing 'assignedProjects' and optional 'avatarUrl' to satisfy the Artist type.
    if (artistToEdit) {
      return {
        ...artistToEdit,
        // Ensure all fields have proper default values
        name: artistToEdit.name || '',
        specialization: artistToEdit.specialization || SPECIALIZATIONS[0],
        software: artistToEdit.software || [],
        availability: artistToEdit.availability || [],
        portfolioUrl: artistToEdit.portfolioUrl || '',
        email: artistToEdit.email || '',
        telegram: artistToEdit.telegram || '',
        contact: artistToEdit.contact || '', // Deprecated: backward compatibility
        avatarUrl: artistToEdit.avatarUrl || '',
        description: artistToEdit.description || '',
        location: artistToEdit.location || '',
        assignedProjects: artistToEdit.assignedProjects || [],
      };
    }
    
    return {
      name: '',
      specialization: SPECIALIZATIONS[0],
      software: [],
      availability: [],
      portfolioUrl: '',
      email: '',
      telegram: '',
      avatarUrl: '',
      description: '',
      location: '',
      assignedProjects: [],
    };
  };

  const [artist, setArtist] = useState(getInitialState());
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showValidation, setShowValidation] = useState(false);
  const [avatarDeleted, setAvatarDeleted] = useState(false); // Track if avatar was explicitly deleted
  const [showOnlyActiveProjects, setShowOnlyActiveProjects] = useState(true);
  const [accessProfile, setAccessProfile] = useState<Artist | null>(null);

  useEffect(() => {
    const initialState = getInitialState();
    setArtist(initialState);
    // Обновляем preview из avatarUrl если он есть (для существующих художников)
    if (initialState.avatarUrl) {
      setAvatarPreview(initialState.avatarUrl);
      setAvatarFile(null); // Сбрасываем файл при открытии модального окна
    } else {
      setAvatarPreview(null);
    }
    setCurrentPage(1); // Reset to first page when modal opens
    setValidationErrors({});
    setShowValidation(false);
    setAvatarDeleted(false);
    setAccessProfile(artistToEdit);
  }, [artistToEdit, isOpen]);

  // Validation function
  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!artist.name.trim()) {
      errors.name = 'Имя обязательно для заполнения';
    }

    if (!artist.email?.trim() && !artist.telegram?.trim()) {
      errors.contact = 'Email или Telegram обязательны для заполнения';
    }

    if (!artist.specialization.trim()) {
      errors.specialization = 'Специализация обязательна';
    }

    // Email format validation
    if (artist.email?.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(artist.email)) {
        errors.email = 'Неверный формат email';
      }
    }

    // Portfolio URL validation
    if (artist.portfolioUrl?.trim()) {
      try {
        new URL(artist.portfolioUrl);
      } catch {
        errors.portfolioUrl = 'Неверный формат URL';
      }
    }

    // Recommendations
    if (artist.software.length === 0) {
      errors.software = 'Рекомендуется указать хотя бы одну программу';
    }

    if (artist.assignedProjects.length === 0) {
      errors.projects = 'Рекомендуется назначить на проект';
    }

    return errors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setArtist(prev => ({ ...prev, [name]: value }));
  };

  const handleSoftwareChange = (softwareName: string) => {
    setArtist(prev => {
      const newSoftware = prev.software.includes(softwareName)
        ? prev.software.filter(s => s !== softwareName)
        : [...prev.software, softwareName];
      return { ...prev, software: newSoftware };
    });
  };

  // Removed handleProjectChange - now handled inline

  const handleDatesChange = (dates: string[]) => {
    setArtist(prev => ({...prev, availability: dates }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setAvatarPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setAvatarFile(null);
      setAvatarPreview(null);
    }
  };



  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Run validation
    const errors = validateForm();
    setValidationErrors(errors);
    setShowValidation(true);
    
    // Check for critical errors (required fields)
    const criticalErrors = Object.entries(errors).filter(([key, _]) => 
      ['name', 'contact', 'specialization', 'email', 'portfolioUrl'].includes(key)
    );
    
    if (criticalErrors.length > 0) {
      // Focus on the first page if there are critical errors there
      const page1Fields = ['name', 'email', 'specialization', 'contact', 'portfolioUrl'];
      const hasPage1Errors = criticalErrors.some(([key, _]) => page1Fields.includes(key));
      
      if (hasPage1Errors && currentPage === 2) {
        setCurrentPage(1);
      }
      
      return; // Don't submit if there are critical errors
    }
    
    // Submitting artist data
    
    // Determine what to send for avatar:
    // - If file selected: send the file
    // - If avatar was deleted: send special "DELETE" string
    // - Otherwise: send undefined (no change)
    let avatarToSend: File | string | undefined = undefined;
    if (avatarFile) {
      avatarToSend = avatarFile;
    } else if (avatarDeleted) {
      avatarToSend = 'DELETE';
    }
    
    onSave({
      ...artist,
      id: artistToEdit ? artistToEdit.id : new Date().toISOString(),
      // Ensure arrays are properly formatted
      software: artist.software || [],
      availability: artist.availability || [],
      assignedProjects: artist.assignedProjects || [],
    } as Artist, avatarToSend as File | undefined);
    // Don't close here - let App.tsx close after successful save
  };

  const renderPage1 = () => (
    <div className="space-y-6">
      <div className="flex items-start space-x-6">
        <div className="flex-shrink-0 relative">
          {(() => {
            // Используем preview если есть файл для загрузки, иначе используем avatarUrl из artist
            const previewToUse = avatarPreview || (artist.avatarUrl && !avatarDeleted ? artist.avatarUrl : null);
            
            // Если preview - это data URL (base64), используем его напрямую
            // Иначе обрабатываем через getAvatarUrl
            let avatarUrl: string | null = null;
            if (previewToUse) {
              if (previewToUse.startsWith('data:')) {
                // Это data URL от FileReader - используем как есть
                avatarUrl = previewToUse;
              } else {
                // Это имя файла или URL - обрабатываем через getAvatarUrl
                avatarUrl = getAvatarUrl(previewToUse);
              }
            }
            
            return avatarUrl && !avatarDeleted ? (
              <div className="relative">
                <img 
                  src={avatarUrl} 
                  alt="Avatar Preview" 
                  className="w-24 h-24 rounded-full object-cover"
                  onError={(e) => {
                    console.log('❌ [ArtistFormModal] Avatar preview failed to load:', avatarUrl);
                    // If avatar fails to load, mark as deleted and show initials
                    setAvatarPreview(null);
                    setAvatarDeleted(true);
                  }}
                  onLoad={() => {
                    // Avatar preview loaded
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setAvatarFile(null);
                    setAvatarPreview(null);
                    setAvatarDeleted(true); // Mark avatar as deleted
                  }}
                  className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                  title="Удалить аватар"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${getAvatarColor(artist.name)} flex items-center justify-center`}>
                <span className="text-white text-2xl font-bold">
                  <span className="avatar-text">{getInitials(artist.name)}</span>
                </span>
              </div>
            );
          })()}
        </div>
        <div className="flex-grow space-y-2">
          <label className="block text-sm font-medium text-gray-300 mb-1">
            {t('artistForm.avatar')}
          </label>
          <div className="space-y-2">
            <input
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            <p className="text-sm text-gray-400">
{t('artistForm.avatarUploadHint')}
            </p>
            <p className="text-xs text-gray-500">
{t('artistForm.avatarAutoHint')}
            </p>
          </div>
        </div>
      </div>

      {/* Contact validation error */}
      {showValidation && validationErrors.contact && (
        <div className="bg-red-900/20 border border-red-500 rounded-md p-3">
          <p className="text-red-400 text-sm">❌ {validationErrors.contact}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input 
          label={t('artistForm.name')} 
          id="name" 
          name="name" 
          value={artist.name} 
          onChange={handleChange} 
          required 
          error={showValidation ? validationErrors.name : undefined}
        />
        <Input 
          label={t('artistForm.email')} 
          id="email" 
          name="email" 
          type="email" 
          value={artist.email} 
          onChange={handleChange} 
          required 
          error={showValidation ? validationErrors.email : undefined}
        />
        <Input 
          label={`${t('artistForm.telegram')} (${t('artistForm.optional')})`} 
          id="telegram" 
          name="telegram" 
          value={artist.telegram || ''} 
          onChange={handleChange} 
          placeholder={t('artistForm.telegramPlaceholder')} 
        />
        <Input 
          label={`${t('artistForm.portfolioUrl')} (${t('artistForm.optional')})`} 
          id="portfolioUrl" 
          name="portfolioUrl" 
          value={artist.portfolioUrl} 
          onChange={handleChange}
          error={showValidation ? validationErrors.portfolioUrl : undefined}
        />
        <Select 
          label={t('artistForm.specialization')} 
          id="specialization" 
          name="specialization" 
          value={artist.specialization} 
          onChange={handleChange}
          required
          error={showValidation ? validationErrors.specialization : undefined}
        >
          <option value="">{t('artistForm.selectSpecialization')}</option>
          {SPECIALIZATIONS.map(spec => <option key={spec} value={spec}>{getSpecializationName(spec)}</option>)}
        </Select>
        <Input
          label={t('artistForm.location')}
          id="location"
          name="location"
          value={artist.location || ''}
          onChange={handleChange}
          placeholder={t('artistForm.locationPlaceholder')}
        />
      </div>

      <Textarea
        label={t('artistForm.description')}
        id="description"
        name="description"
        value={artist.description || ''}
        onChange={handleChange}
        placeholder={t('artistForm.descriptionPlaceholder')}
      />

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
{t('artistForm.software')}
          {showValidation && validationErrors.software && (
            <span className="ml-2 text-yellow-400 text-xs">💡 {validationErrors.software}</span>
          )}
        </label>
        <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 ${
          showValidation && validationErrors.software ? 'border border-yellow-500 rounded-md p-2' : ''
        }`}>
          {SOFTWARE.map(s => (
            <label key={s} className="flex items-center space-x-2 bg-gray-700 p-2 rounded-md cursor-pointer hover:bg-gray-600">
              <input type="checkbox" checked={artist.software.includes(s)} onChange={() => handleSoftwareChange(s)} className="rounded text-indigo-500 focus:ring-indigo-500"/>
              <span className="text-sm text-gray-200">{s}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  // Helper: Check if project is active (current date is within project date range)
  const isProjectActive = (project: Project): boolean => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const startDate = new Date(project.startDate);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(project.endDate);
      endDate.setHours(23, 59, 59, 999);
      
      return today >= startDate && today <= endDate;
    } catch {
      return false;
    }
  };

  // Filter projects based on toggle
  const filteredProjects = showOnlyActiveProjects 
    ? projects.filter(isProjectActive)
    : projects;

  const renderPage2 = () => (
    <div className="space-y-6">
      {projects.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-300">
              {t('artistForm.projects')}
              {showValidation && validationErrors.projects && (
                <span className="ml-2 text-yellow-400 text-xs">💡 {validationErrors.projects}</span>
              )}
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <span className="text-xs text-gray-400">{t('artistForm.showActiveOnly')}</span>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={showOnlyActiveProjects}
                  onChange={(e) => setShowOnlyActiveProjects(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </div>
            </label>
          </div>
          <div 
            className={`grid grid-cols-1 gap-2 bg-gray-700 rounded-md p-3 border ${
              showValidation && validationErrors.projects ? 'border-yellow-500' : 'border-gray-600'
            } ${filteredProjects.length > 6 ? 'max-h-48 overflow-y-auto' : ''}`}
          >
            {filteredProjects.length > 0 ? (
              filteredProjects.map(project => (
                <label key={project.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-600 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={artist.assignedProjects.includes(project.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setArtist(prev => ({
                          ...prev,
                          assignedProjects: [...prev.assignedProjects, project.id]
                        }));
                      } else {
                        setArtist(prev => ({
                          ...prev,
                          assignedProjects: prev.assignedProjects.filter(id => id !== project.id)
                        }));
                      }
                    }}
                    className="rounded text-indigo-500 focus:ring-indigo-500 focus:ring-2"
                  />
                  <span className="text-sm text-gray-200 flex-1">{project.name}</span>
                  <span className="text-xs text-gray-400">
                    {formatProjectDateRange(project.startDate, project.endDate)}
                  </span>
                </label>
              ))
            ) : (
              <p className="text-sm text-gray-400 italic text-center py-2">
                {t('artistForm.noActiveProjects')}
              </p>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {t('artistForm.selectedProjects')}: {artist.assignedProjects.length} {t('common.of')} {showOnlyActiveProjects ? filteredProjects.length : projects.length}
          </p>
        </div>
      )}

      <div>
         <label className="block text-sm font-medium text-gray-300 mb-2">Доступность</label>
         <AvailabilityCalendar selectedDates={artist.availability} onDatesChange={handleDatesChange} />
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={artistToEdit ? t('artistForm.editTitle') : t('artistForm.createTitle')} size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Page Navigation */}
        <div className="flex items-center justify-between border-b border-gray-700 pb-4">
          <div className="flex space-x-1">
            <button
              type="button"
              onClick={() => setCurrentPage(1)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                currentPage === 1 
                  ? 'bg-indigo-600 text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              1. {t('artistForm.personalInfo')}
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage(2)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                currentPage === 2 
                  ? 'bg-indigo-600 text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              2. {t('artistForm.projectsAndAvailability')}
            </button>
          </div>
          <span className="text-xs text-gray-500">
            {t('artistForm.page')} {currentPage} / 2
          </span>
        </div>

        {/* Page Content */}
        <div className="min-h-[400px]">
          {currentPage === 1 ? renderPage1() : renderPage2()}
        </div>

        {artistToEdit && accessProfile && (
          <ProfileAccessSection
            profile={accessProfile}
            onUpdated={(p) => {
              setAccessProfile(p);
              onAccessUpdated?.(p);
            }}
          />
        )}

        {/* Navigation and Submit Buttons */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-700">
          <div className="flex space-x-2">
            {currentPage > 1 && (
              <Button 
                type="button" 
                variant="secondary" 
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                ← {t('artistForm.previous')}
              </Button>
            )}
          </div>
          
          <div className="flex space-x-2">
            <Button type="button" variant="secondary" onClick={onClose}>
{t('artistForm.cancel')}
            </Button>
            
            {currentPage < 2 ? (
              <Button 
                type="button" 
                variant="primary" 
                onClick={() => setCurrentPage(currentPage + 1)}
              >
{t('artistForm.next')} →
              </Button>
            ) : (
              <Button type="submit" variant="primary">
{artistToEdit ? t('artistForm.save') : t('header.addArtist')}
              </Button>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ArtistFormModal;